import { useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { useEffect } from 'react';
import type { ReceivedPaysReportItem } from '@/models/report/types';
import type { Country } from '@/models/country/types';
import { fetchReceivedPaysReport } from '@/models/report/service';
import { fetchCountries } from '@/models/country/service';
import { lastMonthToToday } from '@/lib/dateUtils';
import { parseNumber } from '@/lib/format';
import { currencySign, groupByCountry } from '@/lib/currency';
import { countryStore } from '@/stores/country.store';

export function useReceivedPaysController() {
  const dates = lastMonthToToday();
  const store = useLocalObservable(() => ({
    dateStart: dates.start,
    dateEnd: dates.end,
    idCountries: '',
    items: [] as ReceivedPaysReportItem[],
    // id_account → id_country: в строке отчёта страны нет
    accountCountryIds: {} as Record<number, string>,
    countries: [] as Country[],
    isLoading: false,
    error: null as string | null,
  }));

  async function load() {
    runInAction(() => {
      store.isLoading = true;
      store.error = null;
    });
    try {
      const { items, accountCountryIds } = await fetchWithCountries(
        store.dateStart,
        store.dateEnd,
        store.idCountries
      );
      runInAction(() => {
        store.items = items;
        store.accountCountryIds = accountCountryIds;
      });
    } catch (e) {
      runInAction(() => {
        store.error = (e as Error).message;
      });
    } finally {
      runInAction(() => {
        store.isLoading = false;
      });
    }
  }

  // Страну строки знает только фильтр id_countries бэкенда. Без фильтра отчёт запрашивается
  // целиком и по каждой стране отдельно: так ни одна строка не теряется, а страна берётся
  // из ответа с фильтром.
  async function fetchWithCountries(dateStart: string, dateEnd: string, idCountry: string) {
    const params = { date_start: dateStart, date_end: dateEnd };
    const accountCountryIds: Record<number, string> = {};
    if (idCountry) {
      const items = await fetchReceivedPaysReport({ ...params, id_countries: idCountry });
      for (const item of items) accountCountryIds[item.id_account] = idCountry;
      return { items, accountCountryIds };
    }
    const countries = await countryStore.load();
    const [items, ...byCountry] = await Promise.all([
      fetchReceivedPaysReport(params),
      ...countries.map((country) =>
        fetchReceivedPaysReport({ ...params, id_countries: country.id_country })
      ),
    ]);
    byCountry.forEach((rows, i) => {
      for (const row of rows) accountCountryIds[row.id_account] = countries[i]!.id_country;
    });
    return { items, accountCountryIds };
  }

  useEffect(() => {
    void fetchCountries('1').then((countries) =>
      runInAction(() => {
        store.countries = countries;
      })
    );
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, []);

  function set(field: 'dateStart' | 'dateEnd' | 'idCountries', value: string) {
    runInAction(() => {
      store[field] = value;
    });
  }

  function byServiceTypeChartData() {
    const map = new Map<string, { label: string; count: number; amount: number; color: string }>();
    const colors = ['#3C91E6', '#9FD356', '#FA824C', '#a89fb0'];
    let i = 0;
    for (const item of store.items) {
      const key = item.service_type_name || 'Прочее';
      if (!map.has(key)) {
        map.set(key, {
          label: key,
          count: 0,
          amount: 0,
          color: colors[i++ % colors.length] as string,
        });
      }
      const entry = map.get(key)!;
      entry.count++;
      entry.amount += parseNumber(item.money);
    }
    return Array.from(map.values());
  }

  function countryOf(item: ReceivedPaysReportItem): Country | undefined {
    const idCountry = store.accountCountryIds[item.id_account];
    return idCountry ? countryStore.byId.get(idCountry) : undefined;
  }

  function currencyOf(item: ReceivedPaysReportItem): string {
    return currencySign(countryOf(item));
  }

  function byDateChartData(items: ReceivedPaysReportItem[]) {
    const map = new Map<string, { label: string; amount: number }>();
    for (const item of items) {
      const key = (item.date_of_change || item.date_start_period || '').slice(0, 10);
      if (!key) continue;
      const prev = map.get(key) ?? { label: key, amount: 0 };
      map.set(key, { label: key, amount: prev.amount + parseNumber(item.money) });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }

  const byCountry = groupByCountry(store.items, countryOf);
  const totals = byCountry.map((group) => ({
    ...group,
    amount: group.items.reduce((s, i) => s + parseNumber(i.money), 0),
  }));
  const byDateCharts = byCountry.map((group) => ({ ...group, data: byDateChartData(group.items) }));

  return {
    store,
    set,
    load,
    byServiceTypeChartData,
    countryOf,
    currencyOf,
    totals,
    byDateCharts,
  };
}
